import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPlay, faCheck, faTimes, faCalculator } from '@fortawesome/free-solid-svg-icons';
import { createLocation, getLocations } from '../../services/locationService';
import { calculateLocationDeliveryFee } from '../../services/distanceService';

const LocationSeeder = ({ onComplete }) => {
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingProgress, setSeedingProgress] = useState(0);
  const [seedingStatus, setSeedingStatus] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Comprehensive list of service areas based on existing codebase references
  const comprehensiveLocations = [
    // MAIN OFFICE LOCATIONS
    {
      city: 'Angola',
      state: 'IN',
      zipCodes: ['46703'],
      deliveryFee: 0, // Free delivery from main office
      isActive: true,
      notes: 'Main office location - free delivery within city limits',
    },
    {
      city: 'Elkhart',
      state: 'IN', 
      zipCodes: ['46516', '46514', '46515', '46517'],
      deliveryFee: 0, // Free delivery from main office
      isActive: true,
      notes: 'Main office location - free delivery within city limits',
    },

    // ANGOLA REGION (From AboutPage.js)
    {
      city: 'Fremont',
      state: 'IN',
      zipCodes: ['46737'],
      deliveryFee: 35,
      isActive: true,
      notes: 'Angola service area - close proximity',
    },
    {
      city: 'Hamilton',
      state: 'IN',
      zipCodes: ['46742'],
      deliveryFee: 40,
      isActive: true,
      notes: 'Angola service area',
    },
    {
      city: 'Ashley',
      state: 'IN',
      zipCodes: ['46705'],
      deliveryFee: 45,
      isActive: true,
      notes: 'Angola service area',
    },
    {
      city: 'Pleasant Lake',
      state: 'IN', 
      zipCodes: ['46779'],
      deliveryFee: 40,
      isActive: true,
      notes: 'Angola service area - lake community',
    },
    {
      city: 'Hudson',
      state: 'IN',
      zipCodes: ['46747'],
      deliveryFee: 45,
      isActive: true,
      notes: 'Angola service area',
    },
    {
      city: 'Auburn',
      state: 'IN',
      zipCodes: ['46706'],
      deliveryFee: 45,
      isActive: true,
      notes: 'Additional service area from Angola office',
    },
    {
      city: 'Kendallville',
      state: 'IN',
      zipCodes: ['46755'],
      deliveryFee: 55,
      isActive: true,
      notes: 'Extended Angola service area',
    },
    {
      city: 'Waterloo',
      state: 'IN',
      zipCodes: ['46793'],
      deliveryFee: 40,
      isActive: true,
      notes: 'Angola region - close to Auburn',
    },

    // ELKHART REGION (From AboutPage.js)
    {
      city: 'Goshen',
      state: 'IN',
      zipCodes: ['46526', '46528'],
      deliveryFee: 35,
      isActive: true,
      notes: 'Elkhart service area - close proximity',
    },
    {
      city: 'Middlebury',
      state: 'IN',
      zipCodes: ['46540'],
      deliveryFee: 40,
      isActive: true,
      notes: 'Elkhart service area',
    },
    {
      city: 'Bristol',
      state: 'IN',
      zipCodes: ['46507'],
      deliveryFee: 45,
      isActive: true,
      notes: 'Elkhart service area',
    },
    {
      city: 'Wakarusa',
      state: 'IN',
      zipCodes: ['46573'],
      deliveryFee: 45,
      isActive: true,
      notes: 'Elkhart service area',
    },
    {
      city: 'Nappanee',
      state: 'IN',
      zipCodes: ['46550'],
      deliveryFee: 50,
      isActive: true,
      notes: 'Elkhart service area',
    },
    {
      city: 'Mishawaka',
      state: 'IN',
      zipCodes: ['46544', '46545'],
      deliveryFee: 60,
      isActive: true,
      notes: 'Extended Elkhart service area',
    },

    // MAJOR CITIES (From AboutPage.js and checkout fallback)
    {
      city: 'Fort Wayne',
      state: 'IN',
      zipCodes: ['46804', '46805', '46806', '46807', '46808', '46809', '46815', '46816'],
      deliveryFee: 75,
      isActive: true,
      notes: 'Major service area - multiple ZIP codes',
    },
    {
      city: 'South Bend',
      state: 'IN',
      zipCodes: ['46601', '46614', '46615', '46616', '46617', '46619', '46628'],
      deliveryFee: 65,
      isActive: true,
      notes: 'Major service area',
    },
    {
      city: 'Warsaw',
      state: 'IN',
      zipCodes: ['46580', '46581'],
      deliveryFee: 60,
      isActive: true,
      notes: 'Popular lake area',
    },
    {
      city: 'Columbia City',
      state: 'IN',
      zipCodes: ['46725'],
      deliveryFee: 55,
      isActive: true,
      notes: 'Between Angola and Fort Wayne',
    },

    // MICHIGAN LOCATIONS (From OrdersSection.js filters)
    {
      city: 'Sturgis',
      state: 'MI',
      zipCodes: ['49091'],
      deliveryFee: 65,
      isActive: true,
      notes: 'Cross-border service to Michigan',
    },
    {
      city: 'Coldwater',
      state: 'MI',
      zipCodes: ['49036'],
      deliveryFee: 60,
      isActive: true,
      notes: 'Cross-border service to Michigan',
    },
    {
      city: 'Niles',
      state: 'MI',
      zipCodes: ['49120'],
      deliveryFee: 70,
      isActive: true,
      notes: 'Extended Michigan service area',
    },
    {
      city: 'Dowagiac',
      state: 'MI',
      zipCodes: ['49047'],
      deliveryFee: 75,
      isActive: true,
      notes: 'Extended Michigan service area',
    },

    // ADDITIONAL CURRENT ORDERS LOCATIONS (From OrdersSection.js)
    {
      city: 'Bremen',
      state: 'IN',
      zipCodes: ['46506'],
      deliveryFee: 55,
      isActive: true,
      notes: 'Service area between Elkhart and South Bend',
    },
    {
      city: 'Wolcottville',
      state: 'IN',
      zipCodes: ['46795'],
      deliveryFee: 45,
      isActive: true,
      notes: 'Angola service area - lake community',
    },
    {
      city: 'LaGrange',
      state: 'IN',
      zipCodes: ['46761'],
      deliveryFee: 50,
      isActive: true,
      notes: 'Angola service area',
    }
  ];

  const seedLocations = async () => {
    try {
      setIsSeeding(true);
      setError(null);
      setSeedingStatus('Checking existing locations...');
      
      // Check if any locations already exist
      const existingLocations = await getLocations();
      if (existingLocations.length > 0) {
        setError(`Found ${existingLocations.length} existing locations. Please clear the locations collection first or use this to add additional locations.`);
        setIsSeeding(false);
        return;
      }

      setSeedingStatus('Seeding locations...');
      
      const results = {
        success: [],
        errors: [],
        summary: {
          total: comprehensiveLocations.length,
          mainOffices: 0,
          angolaRegion: 0,
          elkhartRegion: 0,
          majorCities: 0,
          michigan: 0,
          additional: 0
        }
      };

      for (let i = 0; i < comprehensiveLocations.length; i++) {
        const location = comprehensiveLocations[i];
        
        setSeedingStatus(`Adding ${location.city}, ${location.state}...`);
        setSeedingProgress(((i + 1) / comprehensiveLocations.length) * 100);
        
        try {
          // Calculate accurate delivery fee based on distance for this location
          let calculatedLocation = { ...location };
          
          if (location.zipCodes && location.zipCodes.length > 0) {
            try {
              const calculation = await calculateLocationDeliveryFee(
                location.city, 
                location.state, 
                location.zipCodes[0]
              );
              
              // Use calculated fee instead of hardcoded fee
              calculatedLocation.deliveryFee = calculation.fee;
              calculatedLocation.calculation = calculation;
              calculatedLocation.calculatedAt = new Date().toISOString();
              calculatedLocation.isManuallyOverridden = false;
              
              console.log(`📍 ${location.city}: ${calculation.distance}mi from ${calculation.nearestOffice} = $${calculation.fee}`);
            } catch (calcError) {
              console.warn(`Could not calculate fee for ${location.city}, using fallback: ${calcError.message}`);
              // Keep original fallback fee if calculation fails
            }
          }
          
          await createLocation(calculatedLocation);
          results.success.push(`${calculatedLocation.city}, ${calculatedLocation.state} (Fee: $${calculatedLocation.deliveryFee}${calculatedLocation.calculation ? ' - calculated' : ' - fallback'})`);
          
          // Categorize for summary
          if (calculatedLocation.deliveryFee === 0) results.summary.mainOffices++;
          else if (location.notes.includes('Angola service area')) results.summary.angolaRegion++;
          else if (location.notes.includes('Elkhart service area')) results.summary.elkhartRegion++;
          else if (location.notes.includes('Major service area')) results.summary.majorCities++;
          else if (location.state === 'MI') results.summary.michigan++;
          else results.summary.additional++;
          
        } catch (error) {
          console.error(`Error adding ${location.city}:`, error);
          results.errors.push(`${location.city}, ${location.state}: ${error.message}`);
        }
        
        // Small delay to prevent overwhelming the database
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setSeedingStatus('Seeding completed!');
      setResults(results);
      
      // Notify parent component to refresh locations
      if (onComplete && results.success.length > 0) {
        setTimeout(() => {
          onComplete();
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error seeding locations:', error);
      setError(error.message);
    } finally {
      setIsSeeding(false);
    }
  };

  const resetSeeder = () => {
    setSeedingProgress(0);
    setSeedingStatus('');
    setResults(null);
    setError(null);
  };

  return (
    <div style={{
      background: 'white',
      border: '2px solid #e3f2fd',
      borderRadius: '12px',
      padding: '2rem',
      margin: '2rem 0',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ 
          color: '#1976d2', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          margin: '0 0 0.5rem 0'
        }}>
          <FontAwesomeIcon icon={faMapMarkerAlt} />
          Location Seeder
        </h3>
        <p style={{ color: '#666', margin: 0, fontSize: '0.9rem' }}>
          This tool will add all existing service areas from your website to the admin dashboard. 
          <br />
          <strong>Total locations to add: {comprehensiveLocations.length}</strong> (includes Angola region, Elkhart region, major cities, and Michigan areas)
          <br />
          <span style={{ color: '#2e7d32', fontSize: '0.85rem', fontWeight: 'bold' }}>
            <FontAwesomeIcon icon={faCalculator} /> Delivery fees will be automatically calculated based on distance from your offices!
          </span>
        </p>
      </div>

      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #ffcdd2'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {isSeeding ? (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ 
            background: '#e3f2fd',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #bbdefb'
          }}>
            <div style={{ marginBottom: '0.5rem', fontWeight: 'bold', color: '#1976d2' }}>
              {seedingStatus}
            </div>
            <div style={{
              background: '#f5f5f5',
              height: '8px',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                background: '#1976d2',
                height: '100%',
                width: `${seedingProgress}%`,
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ 
              marginTop: '0.5rem', 
              fontSize: '0.9rem', 
              color: '#666' 
            }}>
              {Math.round(seedingProgress)}% complete
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={seedLocations}
            disabled={isSeeding}
            style={{
              background: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faPlay} />
            Seed All Locations
          </button>
          
          {results && (
            <button
              onClick={resetSeeder}
              style={{
                background: '#f5f5f5',
                color: '#666',
                border: '1px solid #e0e0e0',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Reset
            </button>
          )}
        </div>
      )}

      {results && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1.5rem',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h4 style={{ 
            color: '#28a745', 
            margin: '0 0 1rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <FontAwesomeIcon icon={faCheck} />
            Seeding Results
          </h4>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Summary:</div>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
              <li>🏢 Main Offices: {results.summary.mainOffices}</li>
              <li>🌟 Angola Region: {results.summary.angolaRegion}</li>
              <li>🌟 Elkhart Region: {results.summary.elkhartRegion}</li>
              <li>🏙️ Major Cities: {results.summary.majorCities}</li>
              <li>🚗 Michigan: {results.summary.michigan}</li>
              <li>📍 Additional: {results.summary.additional}</li>
            </ul>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <div style={{ 
                fontWeight: 'bold', 
                color: '#28a745', 
                marginBottom: '0.5rem' 
              }}>
                ✅ Successfully Added ({results.success.length}):
              </div>
              <div style={{ 
                maxHeight: '200px', 
                overflow: 'auto', 
                fontSize: '0.85rem',
                color: '#666'
              }}>
                {results.success.map((item, index) => (
                  <div key={index}>• {item}</div>
                ))}
              </div>
            </div>
            
            {results.errors.length > 0 && (
              <div>
                <div style={{ 
                  fontWeight: 'bold', 
                  color: '#dc3545', 
                  marginBottom: '0.5rem' 
                }}>
                  ❌ Errors ({results.errors.length}):
                </div>
                <div style={{ 
                  maxHeight: '200px', 
                  overflow: 'auto', 
                  fontSize: '0.85rem',
                  color: '#dc3545'
                }}>
                  {results.errors.map((item, index) => (
                    <div key={index}>• {item}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#e8f5e9',
            borderRadius: '6px',
            color: '#2e7d32',
            fontSize: '0.9rem'
          }}>
            <strong>🎉 Success!</strong> All service areas from your website are now manageable in the admin dashboard. 
            <br />
            ✅ Delivery fees automatically calculated based on distance from your offices
            <br />
            ✅ You can now edit fees, manage ZIP codes, and activate/deactivate service areas
            <br />
            ✅ Your checkout system will use these calculated fees instead of hardcoded values
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSeeder; 